/**
 * useWorkLifecycleContext — React hook for lifecycle context
 *
 * Provides read/write/clear access to persisted lifecycle context.
 * Keeps state in sync across the session without a global store.
 *
 * BOUNDARY:
 * - persisted context = navigation memory
 * - persisted context ≠ approval
 * - persisted context ≠ execution authority
 */

import { useState, useCallback, useEffect } from 'react';
import {
  loadLifecycleContext,
  saveLifecycleContext,
  clearLifecycleContext,
  checkContextHealth,
  type WorkLifecycleContext,
  type ContextHealthCheck,
} from './WorkLifecycleContext';

interface UseWorkLifecycleContextReturn {
  context: WorkLifecycleContext;
  health: ContextHealthCheck;
  update: (updates: Partial<WorkLifecycleContext>) => void;
  clear: () => void;
}

export function useWorkLifecycleContext(): UseWorkLifecycleContextReturn {
  const [context, setContext] = useState<WorkLifecycleContext>(() => loadLifecycleContext());

  // Re-sync on focus (handles return visits from other tabs)
  useEffect(() => {
    const handleFocus = () => {
      setContext(loadLifecycleContext());
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Re-sync on storage events (handles multi-tab changes)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'trustgate:work-lifecycle-context:v1') {
        setContext(loadLifecycleContext());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const update = useCallback((updates: Partial<WorkLifecycleContext>) => {
    const next = saveLifecycleContext(updates);
    setContext(next);
  }, []);

  const clear = useCallback(() => {
    clearLifecycleContext();
    setContext({});
  }, []);

  const health = checkContextHealth(context);

  return { context, health, update, clear };
}
